import { useApolloClient, useMutation } from '@apollo/client/react'
import { device } from '@homemaster/jslib'
import * as ww from '@wecom/jssdk'
import { type PropsWithChildren, useCallback, useEffect, useState } from 'react'
import VConsole from 'vconsole'
import { getAppInfo } from '../config'
import {
  BasicUserFieldsFragmentDoc,
  GetCurrentUserDocument,
  UserLoginDocument,
} from '../gql/graphql'
import { getAgentConfigSignature } from '../wxwork'
import LoginDialog from './LoginDialog'

function registerWxwork() {
  const appInfo = getAppInfo()

  ww.register({
    corpId: appInfo.wxwork_corpid,
    agentId: appInfo.wxwork_agentid,
    getAgentConfigSignature,
    jsApiList: [
      'getCurExternalContact',
      'getCurExternalChat',
      'getContext',
      'sendChatMessage',
    ],
  })
}

export default function LoginRedirector({ children }: PropsWithChildren) {
  const apolloClient = useApolloClient()
  const [state, setState] = useState<string>()
  const [login] = useMutation(UserLoginDocument)

  useEffect(() => {
    if (device.getType() === 'wxwork') {
      const urlParams = new URLSearchParams(window.location.search)
      const authToken = urlParams.get('auth_token')
      const appInfo = getAppInfo()

      if (appInfo.deployEnv !== 'prod') {
        new VConsole()
      }

      if (authToken) {
        // removes the auth_token from
        const url = new URL(window.location.href)
        url.search = ''
        history.replaceState(null, '', url.href)
        localStorage.setItem('authToken', authToken)

        ;(async () => {
          try {
            const { data } = await login()

            if (!data?.login) {
              console.error('Login failed: No data received')
              setState('error')
              return
            }

            // this is a new authToken as the initial one embedded in the HTML
            // would not be safe
            localStorage.setItem('authToken', data.login.authToken)

            apolloClient.writeQuery({
              query: GetCurrentUserDocument,
              data: {
                currentUser: apolloClient.cache.readFragment({
                  id: apolloClient.cache.identify(data.login.user),
                  fragment: BasicUserFieldsFragmentDoc,
                }),
              },
            })
            registerWxwork()
            setState('ready')
          } catch (error) {
            console.error('Login error:', error)
            setState('error')
          }
        })()
      } else {
        setState('error')
      }
    } else {
      setState('qrlogin')
    }
  }, [login, apolloClient])

  const onLoginSuccess = useCallback(() => {
    setState('ready')
  }, [])

  switch (state) {
    case 'qrlogin':
      return <LoginDialog onLoginSuccess={onLoginSuccess} />
    case 'ready':
      return children
    case 'error':
      return <div>无法登陆</div>
    case undefined:
      return null
    default:
      throw new Error('Invalid LoginRedirector state')
  }
}
